// Конструктор тура: чистая логика без React и хранилища — позиции, дни, перенос, подсказки «чего не хватает»,
// итоги по видам и валютам, пересчёт по курсам, расстояния между точками дня.
import type { Plan, PlanItem, PlanItemKind, PlanItemStatus } from '@sayohat/shared';
import { addDaysIso, daysBetween } from '../../shared/lib/format';
import type { TripItem } from '../../shared/lib/types';

/** План в браузере: id и ключ появляются после «Сохранить и поделиться» */
export interface LocalPlan {
  id: string | null;
  editKey: string | null;
  /** версия на сервере, от которой сделаны правки (совместная правка) */
  rev?: number | null;
  title: string;
  cities: string[];
  startDate: string | null;
  endDate: string | null;
  travelers: { adults: number; children: number };
  budget: { amount: number | null; currency: string };
  items: PlanItem[];
  notes: string;
  updatedAt: string;
}

/** Позиция прежней «Моей поездки» (localStorage 'trip') */
export interface LegacyTripItem {
  id?: string;
  kind?: string;
  title?: string;
  details?: string;
  price?: number | null;
  currency?: string;
  url?: string | null;
  addedAt?: string;
}

export const KINDS: PlanItemKind[] = ['flight', 'train', 'bus', 'transfer', 'taxi', 'car', 'hotel', 'attraction', 'activity', 'restaurant', 'note'];
export const STATUSES: PlanItemStatus[] = ['idea', 'chosen', 'booked'];
/** Транспорт между городами */
const INTERCITY: PlanItemKind[] = ['flight', 'train', 'bus', 'car'];
/** Дорога из аэропорта */
const LOCAL_RIDE: PlanItemKind[] = ['transfer', 'taxi', 'car'];
const MAX_ITEMS = 200;
const MAX_DAYS = 60;

/** Цвета дней на карте и в ленте */
export const DAY_COLORS = ['#2F6BFF', '#FF5A36', '#0E9F6E', '#B7791F', '#8B5CF6', '#E11D74', '#0891B2', '#65A30D'];

export const emptyPlan = (): LocalPlan => ({
  id: null, editKey: null, title: '', cities: [], startDate: null, endDate: null,
  travelers: { adults: 2, children: 0 }, budget: { amount: null, currency: 'usd' }, items: [], notes: '', updatedAt: '',
});

let seq = 0;
export const newId = (): string => `${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const isDay = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const isTime = (s: unknown): s is string => typeof s === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const https = (s: unknown): string | null => (typeof s === 'string' && /^https:\/\//.test(s) ? s : null);
const str = (s: unknown, max: number): string => (typeof s === 'string' ? s.trim().slice(0, max) : '');

/** Позиция из события шины 'trip:add' (или из старой поездки) → позиция плана */
export function normalizeItem(raw: Partial<TripItem & PlanItem>): PlanItem {
  const kind = (KINDS as string[]).includes(String(raw.kind)) ? (raw.kind as PlanItemKind) : 'note';
  const date = isDay(raw.date) ? raw.date : null;
  const endDate = isDay(raw.endDate) && date && raw.endDate >= date ? raw.endDate : null;
  const lat = num(raw.lat), lon = num(raw.lon);
  const price = num(raw.price);
  return {
    id: typeof raw.id === 'string' && /^[\w-]{1,40}$/.test(raw.id) ? raw.id : newId(),
    kind,
    title: str(raw.title, 200) || '—',
    details: str(raw.details, 1000),
    price: price !== null && price > 0 ? price : null,
    currency: str(raw.currency, 3).toLowerCase() || 'usd',
    url: https(raw.url),
    date,
    time: isTime(raw.time) ? raw.time : null,
    endDate,
    durationMin: num(raw.durationMin),
    lat: lat !== null && lon !== null ? lat : null,
    lon: lat !== null && lon !== null ? lon : null,
    city: str(raw.city, 100) || null,
    image: https(raw.image),
    refId: str(raw.refId, 200) || null,
    status: (STATUSES as string[]).includes(String(raw.status)) ? (raw.status as PlanItemStatus) : 'idea',
    note: str(raw.note, 1000),
    locked: raw.locked === true,
    hours: str(raw.hours, 300) || null,
    ...optionalFields(raw),
  };
}

/** Часовой пояс IANA, который знает браузер; иначе null */
export function validZone(v: unknown): string | null {
  const s = str(v, 60);
  if (!s || !/^(UTC|[A-Za-z]+(\/[A-Za-z0-9_+-]+){1,2})$/.test(s)) return null;
  try { new Intl.DateTimeFormat('en-US', { timeZone: s }); return s; } catch { return null; }
}

/** Необязательные поля позиции — только если заданы (часовой пояс, бронь на сайте, цена за человека) */
function optionalFields(raw: Partial<TripItem & PlanItem>): Pick<PlanItem, 'tz' | 'bookingId' | 'priceFor'> {
  const out: Pick<PlanItem, 'tz' | 'bookingId' | 'priceFor'> = {};
  const tz = validZone(raw.tz);
  if (tz) out.tz = tz;
  if (typeof raw.bookingId === 'string' && /^[\w-]{4,20}$/.test(raw.bookingId)) out.bookingId = raw.bookingId;
  if (raw.priceFor === 'person') out.priceFor = 'person';
  return out;
}

/** Одна и та же услуга — не добавляем дважды */
const sameItem = (a: PlanItem, b: PlanItem): boolean =>
  a.kind === b.kind && a.title === b.title && a.details === b.details && a.price === b.price && a.date === b.date && (a.refId || '') === (b.refId || '');

/** Даты и города плана растут вместе с позициями */
function widen(plan: LocalPlan, it: PlanItem): LocalPlan {
  let { startDate, endDate } = plan;
  const last = it.endDate || it.date;
  if (it.date && (!startDate || it.date < startDate)) startDate = it.date;
  if (last && (!endDate || last > endDate)) endDate = last;
  const cities = it.city && !plan.cities.some((c) => c.toLowerCase() === it.city!.toLowerCase()) ? [...plan.cities, it.city].slice(0, 20) : plan.cities;
  return { ...plan, startDate, endDate, cities };
}

export type AddResult = { plan: LocalPlan; added: boolean; reason?: 'duplicate' | 'full' };

export function addItem(plan: LocalPlan, raw: Partial<TripItem & PlanItem>): AddResult {
  const it = normalizeItem({ ...raw, id: undefined });
  if (plan.items.some((x) => sameItem(x, it))) return { plan, added: false, reason: 'duplicate' };
  if (plan.items.length >= MAX_ITEMS) return { plan, added: false, reason: 'full' };
  return { plan: widen({ ...plan, items: [...plan.items, it] }, it), added: true };
}

/** Несколько позиций сразу (пакет тура, «Скопировать в мой тур») */
export function addItems(plan: LocalPlan, list: Partial<TripItem & PlanItem>[]): { plan: LocalPlan; added: number } {
  let p = plan, added = 0;
  for (const raw of list) {
    const r = addItem(p, raw);
    p = r.plan;
    if (r.added) added++;
  }
  return { plan: p, added };
}

/** Перенос старой «Моей поездки» в план: позиции без дат попадают в «Без даты» */
export function migrateTrip(old: unknown): LocalPlan {
  const list = Array.isArray(old) ? (old as LegacyTripItem[]) : [];
  return addItems(emptyPlan(), list.filter((x) => x && typeof x === 'object' && x.title).map((x) => ({
    kind: x.kind as PlanItemKind, title: x.title, details: x.details || '', price: x.price ?? null, currency: x.currency || 'usd', url: x.url || null, status: 'chosen' as const,
  }))).plan;
}

/** План из localStorage (или с сервера) → проверенный LocalPlan */
export function restorePlan(raw: unknown): LocalPlan {
  const base = emptyPlan();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<LocalPlan>;
  const items = Array.isArray(r.items) ? r.items.slice(0, MAX_ITEMS).map((x) => normalizeItem(x)) : [];
  const ids = new Set<string>();
  for (const it of items) { if (ids.has(it.id)) it.id = newId(); ids.add(it.id); }
  const t = (r.travelers || {}) as Partial<LocalPlan['travelers']>;
  const b = (r.budget || {}) as Partial<LocalPlan['budget']>;
  return {
    id: typeof r.id === 'string' && /^[\w-]{4,20}$/.test(r.id) ? r.id : null,
    editKey: typeof r.editKey === 'string' ? r.editKey : null,
    rev: typeof r.rev === 'number' ? r.rev : null,
    title: str(r.title, 120),
    cities: Array.isArray(r.cities) ? r.cities.map((c) => str(c, 100)).filter(Boolean).slice(0, 20) : [],
    startDate: isDay(r.startDate) ? r.startDate : null,
    endDate: isDay(r.endDate) ? r.endDate : null,
    travelers: { adults: clampInt(t.adults, 1, 20, 2), children: clampInt(t.children, 0, 20, 0) },
    budget: { amount: num(b.amount) !== null && Number(b.amount) > 0 ? Math.round(Number(b.amount)) : null, currency: str(b.currency, 3).toLowerCase() || 'usd' },
    items,
    notes: str(r.notes, 3000),
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
  };
}

const clampInt = (v: unknown, min: number, max: number, def: number): number => {
  const n = num(v);
  return n === null ? def : Math.max(min, Math.min(max, Math.round(n)));
};

/** Тело запроса к /api/plans (без id и ключа) */
export function toPlanInput(p: LocalPlan): Omit<Plan, 'id' | 'createdAt' | 'updatedAt' | 'editKey'> {
  return { title: p.title, cities: p.cities, startDate: p.startDate, endDate: p.endDate, travelers: p.travelers, budget: p.budget, items: p.items, notes: p.notes };
}

/** План с сервера → LocalPlan (без ключа — только просмотр) */
export const fromPlan = (p: Plan, editKey: string | null = null): LocalPlan => ({ ...restorePlan(p), id: p.id, editKey });

// ---------- Брони на сайте ----------

/** Бронь подтверждена (или уже оплачена, выполнена) — в туре «забронировано» */
export const bookedStatus = (s: string): boolean => s === 'confirmed' || s === 'paid' || s === 'done';

/** Бронь — часть этой поездки: тот же город или даты рядом с датами тура */
export function belongsToTrip(plan: LocalPlan, b: { city?: string | null; date?: string | null }): boolean {
  if (!plan.items.length) return false;
  const city = String(b.city || '').trim().toLowerCase();
  if (city && (plan.cities.some((c) => c.toLowerCase() === city) || plan.items.some((x) => (x.city || '').toLowerCase() === city))) return true;
  const start = plan.startDate, end = plan.endDate || plan.startDate;
  return Boolean(b.date && start && end && b.date >= addDaysIso(start, -1) && b.date <= addDaysIso(end, 1));
}

/** Бронь из окна брони → тур: позиция, из которой бронировали, получает номер брони; бронь этой поездки — новая позиция */
export function applyBooking(plan: LocalPlan, e: {
  booking: { id: string; status: string; date: string | null; endDate: string | null; time: string | null; total?: { amount: number; currency: string } | null };
  req: {
    kind: string; source?: string; id?: string | null; title: string; details?: string; price?: number | null; currency?: string;
    url?: string | null; image?: string | null; city?: string | null; lat?: number | null; lon?: number | null; tripItemId?: string | null;
  };
}, force = false): { plan: LocalPlan; action: 'linked' | 'added' | 'none' } {
  const { booking: b, req } = e;
  const status: PlanItemStatus = bookedStatus(b.status) ? 'booked' : 'chosen';
  const linked = req.tripItemId ? plan.items.find((x) => x.id === req.tripItemId) : undefined;
  if (linked) {
    return { plan: updateItem(plan, linked.id, { bookingId: b.id, status, date: b.date || linked.date, endDate: b.endDate || linked.endDate, time: b.time || linked.time }), action: 'linked' };
  }
  if (plan.items.some((x) => x.bookingId === b.id)) return { plan, action: 'none' };
  if (!force && !belongsToTrip(plan, { city: req.city, date: b.date })) return { plan, action: 'none' };
  const r = addItem(plan, {
    kind: (KINDS as string[]).includes(req.kind) ? (req.kind as PlanItemKind) : 'activity',
    title: req.title,
    details: [req.details, `бронь №${b.id}`].filter(Boolean).join(', '),
    price: b.total ? b.total.amount : req.price ?? null,
    currency: (b.total ? b.total.currency : req.currency || 'usd').toLowerCase(),
    url: req.url ?? null, image: req.image ?? null, city: req.city ?? null, lat: req.lat ?? null, lon: req.lon ?? null,
    refId: req.id && req.source ? `${req.source}-${req.id}` : null,
    date: b.date, endDate: b.endDate, time: b.time, bookingId: b.id, status,
  });
  return r.added ? { plan: r.plan, action: 'added' } : { plan, action: 'none' };
}

// ---------- Изменения позиций ----------

export const updateItem = (plan: LocalPlan, id: string, patch: Partial<PlanItem>): LocalPlan => {
  const items = plan.items.map((x) => (x.id === id ? normalizeItem({ ...x, ...patch, id: x.id }) : x));
  const it = items.find((x) => x.id === id);
  return it ? widen({ ...plan, items }, it) : plan;
};

export const removeItem = (plan: LocalPlan, id: string): LocalPlan => ({ ...plan, items: plan.items.filter((x) => x.id !== id) });

/** Копия позиции сразу после оригинала (например, тот же ресторан в другой день) */
export function duplicateItem(plan: LocalPlan, id: string): LocalPlan {
  const i = plan.items.findIndex((x) => x.id === id);
  if (i < 0 || plan.items.length >= MAX_ITEMS) return plan;
  const copy = { ...plan.items[i], id: newId(), status: 'idea' as const, locked: false };
  return { ...plan, items: [...plan.items.slice(0, i + 1), copy, ...plan.items.slice(i + 1)] };
}

/**
 * Перенести позицию на день (null — «Без даты»); beforeId — поставить перед этой позицией.
 * Отель сохраняет число ночей: выезд сдвигается вместе с заездом.
 */
export function moveItem(plan: LocalPlan, id: string, date: string | null, beforeId?: string | null): LocalPlan {
  const it = plan.items.find((x) => x.id === id);
  if (!it || id === beforeId) return plan;
  let endDate: string | null = null;
  if (date && it.date && it.endDate) endDate = addDaysIso(date, daysBetween(it.date, it.endDate));
  const target = beforeId ? plan.items.find((x) => x.id === beforeId) : null;
  // Перед позицией с временем — берём её время, чтобы порядок в дне сохранился
  const time = target && target.time && target.date === date ? target.time : it.time;
  const moved = { ...it, date, endDate, time };
  const rest = plan.items.filter((x) => x.id !== id);
  const at = beforeId ? rest.findIndex((x) => x.id === beforeId) : -1;
  const items = at < 0 ? [...rest, moved] : [...rest.slice(0, at), moved, ...rest.slice(at)];
  return widen({ ...plan, items }, moved);
}

// ---------- Дни ----------

export interface PlanDay {
  /** null — «Без даты» */
  date: string | null;
  /** номер дня поездки с 1 (0 — без даты) */
  n: number;
  items: PlanItem[];
  color: string;
}

/** Позиции дня: сначала со временем по порядку, затем без времени в порядке добавления */
export function sortDay(items: PlanItem[]): PlanItem[] {
  const timed = items.filter((x) => x.time).sort((a, b) => a.time!.localeCompare(b.time!));
  return [...timed, ...items.filter((x) => !x.time)];
}

/** Все дни поездки (включая пустые между началом и концом) и «Без даты» в конце (если есть такие позиции или emptyBucket) */
export function planDays(plan: LocalPlan, emptyBucket = false): PlanDay[] {
  const dates = new Set<string>();
  if (plan.startDate && plan.endDate && plan.endDate >= plan.startDate) {
    const n = Math.min(MAX_DAYS, daysBetween(plan.startDate, plan.endDate) + 1);
    for (let i = 0; i < n; i++) dates.add(addDaysIso(plan.startDate, i));
  } else if (plan.startDate) dates.add(plan.startDate);
  for (const it of plan.items) if (it.date) dates.add(it.date);
  const sorted = [...dates].sort();
  const first = sorted[0];
  const days: PlanDay[] = sorted.map((date, i) => ({
    date, n: daysBetween(first, date) + 1, items: sortDay(plan.items.filter((x) => x.date === date)), color: DAY_COLORS[i % DAY_COLORS.length],
  }));
  const undated = plan.items.filter((x) => !x.date);
  if (undated.length || emptyBucket) days.push({ date: null, n: 0, items: undated, color: '#8E99AE' });
  return days;
}

// ---------- Расстояния ----------

/** Расстояние по прямой, км */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface MoveHint { km: number; mode: 'walk' | 'car'; min: number }

/**
 * Дорога между точками (≈): по прямой × 1,3. До 1,5 км по прямой — пешком (4,5 км/ч),
 * дальше — на машине (по городу 25 км/ч, за городом, от 40 км, — 70 км/ч), с округлением до 5 мин.
 */
export function hop(a: { lat: number; lon: number }, b: { lat: number; lon: number }): MoveHint {
  const straight = distanceKm(a, b);
  const km = straight * 1.3;
  if (straight <= 1.5) return { km, mode: 'walk', min: Math.max(1, Math.round((km / 4.5) * 60)) };
  const speed = straight > 40 ? 70 : 25;
  return { km, mode: 'car', min: Math.max(5, Math.round(((km / speed) * 60) / 5) * 5) };
}

/** Подсказка между соседними точками дня (та же оценка дороги, что у автосборки маршрута) */
export function moveHint(a: PlanItem, b: PlanItem): MoveHint | null {
  if (a.lat === null || a.lon === null || b.lat === null || b.lon === null) return null;
  if (distanceKm({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon }) < 0.05) return null;
  return hop({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
}

// ---------- Подсказки «чего не хватает» ----------

export type Suggestion =
  | { type: 'hotel'; checkIn: string; checkOut: string; nights: number; city: string | null }
  | { type: 'transfer'; date: string; city: string | null; title: string }
  | { type: 'transport'; date: string | null; from: string; to: string };

/** Город, где путешественник ночует в этот день: последний известный город на этот день или раньше */
function cityOn(plan: LocalPlan, day: string): string | null {
  const known = plan.items.filter((x) => x.city && x.date && x.date <= day)
    .sort((a, b) => `${a.date} ${a.time || '99:99'}`.localeCompare(`${b.date} ${b.time || '99:99'}`));
  return known.length ? known[known.length - 1].city : plan.cities[0] || null;
}

export function suggestions(plan: LocalPlan): Suggestion[] {
  const out: Suggestion[] = [];
  const dated = plan.items.filter((x) => x.date);
  const first = plan.startDate || dated.map((x) => x.date!).sort()[0];
  const last = plan.endDate || dated.map((x) => x.endDate || x.date!).sort().slice(-1)[0];

  // 1. Ночи без отеля: от первого до предпоследнего дня
  if (first && last && last > first) {
    const hotels = plan.items.filter((x) => x.kind === 'hotel' && x.date);
    const covered = (night: string) => hotels.some((h) => h.date! <= night && night < (h.endDate || addDaysIso(h.date!, 1)));
    const nights = Math.min(MAX_DAYS, daysBetween(first, last));
    let run: string[] = [];
    const flush = () => {
      if (run.length) out.push({ type: 'hotel', checkIn: run[0], checkOut: addDaysIso(run[run.length - 1], 1), nights: run.length, city: cityOn(plan, run[0]) });
      run = [];
    };
    for (let i = 0; i < nights; i++) {
      const night = addDaysIso(first, i);
      // Ночь в поезде или самолёте тоже не требует отеля, но об этом судить сложно — предлагаем отель
      if (covered(night) || (run.length && cityOn(plan, night) !== cityOn(plan, run[0]))) flush();
      if (!covered(night)) run.push(night);
    }
    flush();
  }

  // 2. Прилёт без трансфера: в день перелёта нет трансфера, такси или авто
  for (const f of plan.items.filter((x) => x.kind === 'flight' && x.date)) {
    const hasRide = plan.items.some((x) => LOCAL_RIDE.includes(x.kind) && (x.date === f.date || (f.endDate && x.date === f.endDate)));
    if (!hasRide) out.push({ type: 'transfer', date: f.date!, city: f.city, title: f.title });
  }

  // 3. Смена города без транспорта между ними
  const withCity = dated.filter((x) => x.city && !INTERCITY.includes(x.kind))
    .sort((a, b) => `${a.date} ${a.time || '99:99'}`.localeCompare(`${b.date} ${b.time || '99:99'}`));
  for (let i = 1; i < withCity.length; i++) {
    const a = withCity[i - 1], b = withCity[i];
    if (a.city!.toLowerCase() === b.city!.toLowerCase()) continue;
    const moved = plan.items.some((x) => INTERCITY.includes(x.kind) && x.date && x.date >= a.date! && x.date <= b.date!);
    if (!moved && !out.some((s) => s.type === 'transport' && s.from === a.city && s.to === b.city)) out.push({ type: 'transport', date: b.date, from: a.city!, to: b.city! });
  }
  return out;
}

// ---------- Итоги ----------

/** Курсы: код валюты (верхний регистр) → сумов за 1 единицу */
export type Rates = Record<string, number>;

export function convert(v: number, from: string, to: string, rates: Rates | null): number | null {
  if (from.toLowerCase() === to.toLowerCase()) return v;
  if (!rates) return null;
  const a = rates[from.toUpperCase()], b = rates[to.toUpperCase()];
  return a && b ? (v * a) / b : null;
}

export interface Totals {
  /** [валюта, сумма] по убыванию числа позиций */
  byCurrency: [string, number][];
  byKind: { kind: PlanItemKind; count: number; byCurrency: [string, number][] }[];
  /** сумма в валюте бюджета, если все цены удалось пересчитать */
  inBudget: number | null;
  /** суммы, которые не удалось пересчитать в валюту бюджета */
  unconverted: [string, number][];
  priced: number;
}

/** Стоимость позиции для всей поездки: цена «за человека» × число путешественников, иначе — как указана */
export function itemCost(plan: Pick<LocalPlan, 'travelers'>, x: Pick<PlanItem, 'price' | 'priceFor'>): number {
  if (!x.price) return 0;
  return x.priceFor === 'person' ? x.price * Math.max(1, plan.travelers.adults + plan.travelers.children) : x.price;
}

const sumBy = (plan: Pick<LocalPlan, 'travelers'>, items: PlanItem[]): [string, number][] => {
  const m = new Map<string, number>();
  for (const x of items) if (x.price) m.set(x.currency, (m.get(x.currency) || 0) + itemCost(plan, x));
  return [...m.entries()];
};

export function totals(plan: LocalPlan, rates: Rates | null = null): Totals {
  const priced = plan.items.filter((x) => x.price);
  const cur = plan.budget.currency;
  let inBudget = 0;
  const unconverted = new Map<string, number>();
  for (const x of priced) {
    const cost = itemCost(plan, x);
    const v = convert(cost, x.currency, cur, rates);
    if (v === null) unconverted.set(x.currency, (unconverted.get(x.currency) || 0) + cost);
    else inBudget += v;
  }
  const kinds = KINDS.filter((k) => plan.items.some((x) => x.kind === k));
  return {
    byCurrency: sumBy(plan, priced),
    byKind: kinds.map((kind) => {
      const list = plan.items.filter((x) => x.kind === kind);
      return { kind, count: list.length, byCurrency: sumBy(plan, list) };
    }),
    inBudget: unconverted.size ? null : Math.round(inBudget),
    unconverted: [...unconverted.entries()],
    priced: priced.length,
  };
}

/** Доля бюджета: потрачено (только пересчитанное в валюту бюджета) и процент */
export function budgetUse(plan: LocalPlan, rates: Rates | null = null): { spent: number; pct: number; over: boolean } | null {
  const amount = plan.budget.amount;
  if (!amount) return null;
  let spent = 0;
  for (const x of plan.items) {
    if (!x.price) continue;
    const v = convert(itemCost(plan, x), x.currency, plan.budget.currency, rates);
    if (v !== null) spent += v;
  }
  spent = Math.round(spent);
  return { spent, pct: Math.min(100, Math.round((spent / amount) * 100)), over: spent > amount };
}

/** Через сколько дней начинается скопированный тур, если его даты уже прошли */
export const COPY_AHEAD_DAYS = 30;

/** Копия чужого тура (страница /plan/<id>): прошедшие даты сдвигаются вперёд целиком (порядок дней сохраняется),
 *  чужие брони становятся «выбрано» — бронировать нужно самому. shifted — на сколько дней сдвинуто */
export function copyPlan(plan: LocalPlan, todayIso: string): { plan: LocalPlan; shifted: number } {
  const first = plan.startDate || plan.items.map((x) => x.date).filter((d): d is string => Boolean(d)).sort()[0] || null;
  const shifted = first && first < todayIso ? daysBetween(first, addDaysIso(todayIso, COPY_AHEAD_DAYS)) : 0;
  const move = (d: string | null): string | null => (d && shifted ? addDaysIso(d, shifted) : d);
  return {
    shifted,
    plan: {
      ...plan,
      startDate: move(plan.startDate),
      endDate: move(plan.endDate),
      items: plan.items.map((x) => ({
        ...x, date: move(x.date), endDate: move(x.endDate),
        status: x.status === 'booked' ? 'chosen' : x.status, bookingId: null,
      })),
    },
  };
}
